import moment from 'moment';
import {
  createLesson,
  getLessonById,
  getCourseClassTimeMembers,
  checkPasteTrainer,
  previewGroupReserve,
} from '../../../api/lesson';
import { createConsultation, getConsultationById } from '../../../api/consultation';
import { createBranchSchedule, getBranchScheduleById } from '../../../api/branchSchedule';

const DAY_NAMES = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
const DAY_END_MINUTES = 24 * 60;

export const PRIVATE_KINDS = ['PRIVATE_LESSON', 'DUET_LESSON'];

/** 우클릭 복사 가능한 카드 유형. 이 밖(온라인 등)은 메뉴를 띄우지 않는다. */
export const COPYABLE_KINDS = [...PRIVATE_KINDS, 'OT', 'GROUP_LESSON', 'CONSULTATION', 'ETC'];

const pad = value => String(value).padStart(2, '0');

const toMinutes = time => {
  const [hour, minute] = String(time || '00:00').split(':');
  return Number(hour) * 60 + Number(minute || 0);
};

const toTime = minutes => `${pad(Math.floor(minutes / 60) % 24)}:${pad(minutes % 60)}`;

const dayOfWeekOf = date => DAY_NAMES[moment(date, 'YYYY-MM-DD').day()];

/**
 * 카드에서 클립보드에 담을 것만 추린다. 원본 상세는 붙여넣는 순간 API 로 다시 읽는다 —
 * 복사한 뒤 원본을 수정했으면 최신 내용이 붙어야 한다.
 */
export const buildClipboardEntry = event => ({
  kind: event.courseClassType,
  title: event.className,
  startTime: event.startTime,
  endTime: event.endTime,
  durationMinutes: Math.max(10, toMinutes(event.endTime) - toMinutes(event.startTime)),
  sourceDate: event.registrationStartDate || event.classDate,
  courseClassId: event.courseClassId,
  courseClassTimeId: event.courseClassTimeId,
  consultationId: event.consultationId,
  branchScheduleId: event.branchScheduleId,
  trainerId: event.trainerId,
  trainerName: event.trainerName,
  memberId: event.memberId,
  memberName: event.memberName,
  memo: event.memo,
  exerciseRoomId: event.exerciseRoomId,
  tickets: event.tickets,
  // 지점 스케줄은 membersMaxCount, 그룹수업 화면은 maxMemberCount 로 정원을 싣는다.
  capacity: event.membersMaxCount ?? event.maxMemberCount ?? null,
  copiedAt: Date.now(),
});

/**
 * 붙여넣을 시각. 시(hour)를 준 보기(일·주·그룹수업)는 그 정시에서 원본 길이만큼,
 * 안 준 보기(월간)는 원본 시간을 그대로 쓴다.
 * target.startTime('HH:mm')이 있으면 분 단위 시작 시각으로 우선한다(일간 드래그 복사).
 */
const resolveTimes = (entry, target) => {
  if (target.startTime) {
    const startMinutes = toMinutes(target.startTime);
    const endMinutes = startMinutes + entry.durationMinutes;
    if (endMinutes >= DAY_END_MINUTES) {
      throw new Error(
        `${entry.durationMinutes}분 일정을 ${target.startTime}에 붙여넣으면 자정을 넘깁니다. 더 이른 시간에 붙여넣어 주세요.`,
      );
    }
    return { startTime: toTime(startMinutes), endTime: toTime(endMinutes) };
  }
  if (target.startHour == null) {
    return { startTime: entry.startTime, endTime: entry.endTime };
  }
  const startMinutes = Number(target.startHour) * 60;
  const endMinutes = startMinutes + entry.durationMinutes;
  // 수업 시각은 하루 안에서만 표현된다(종료가 자정을 넘으면 END < START 가 되어 겹침 검증이 무력화된다).
  if (endMinutes >= DAY_END_MINUTES) {
    throw new Error(
      `${entry.durationMinutes}분 일정을 ${pad(target.startHour)}시에 붙여넣으면 자정을 넘깁니다. 더 이른 시간에 붙여넣어 주세요.`,
    );
  }
  return { startTime: toTime(startMinutes), endTime: toTime(endMinutes) };
};

/**
 * 붙여넣을 회차가 이미 끝났는가(종료 시각 < 지금). 지난 시간으로의 복사·붙여넣기는 막는다(사용자 확정 2026-09-30).
 */
export const PAST_PASTE_MESSAGE = '지난 시간으로는 복사·붙여넣기할 수 없습니다.';

export const isPastPasteTarget = (entry, target) => {
  const { endTime } = resolveTimes(entry, target);
  return moment(`${target.date} ${endTime}`, 'YYYY-MM-DD HH:mm').isBefore(moment());
};

/**
 * 수업에 연결된 수강권 상품 ID. 구버전 수업은 course_class_ticket 매핑 없이
 * course_class.COURSE_TICKET_ID 하나만 갖고 있어 상세 응답의 tickets 가 비고 courseTicketId 만 온다.
 */
const productTicketIds = lesson => {
  const mapped = (lesson?.tickets || []).map(ticket => ticket.ticketId).filter(Boolean);
  if (mapped.length > 0) return mapped;
  return lesson?.courseTicketId ? [lesson.courseTicketId] : [];
};

const trialTicketIds = lesson =>
  (lesson?.trialTicketList || []).map(ticket => ticket.trialTicketId).filter(Boolean);

/** 상품이 하나도 안 붙은 일정은 생성 자체가 거부된다 — 서버 메시지 대신 무엇을 해야 하는지 알려준다. */
const requireTickets = (tickets, trials) => {
  if (tickets.length === 0 && trials.length === 0) {
    throw new Error('이 일정에는 연결된 수강권이 없어 붙여넣을 수 없습니다. 일정을 열어 수강권을 지정한 뒤 다시 복사해 주세요.');
  }
};

/** 붙여넣기 담당자. 강사 컬럼이 있는 일간 보기만 대상 강사가 바뀌고 나머지는 원본 담당자다. */
const resolveTrainer = (entry, target) => ({
  trainerId: target.trainerId ?? entry.trainerId,
  trainerName: target.trainerId ? target.trainerName : entry.trainerName,
});

/**
 * 붙여넣는 순간 다시 읽은 원본의 강사·시간으로 복사 시점 스냅샷을 덮는다 —
 * 복사한 뒤 원본을 옮기거나 담당자를 바꿨어도 최신 값이 붙어야 한다. 값이 없으면 스냅샷을 그대로 쓴다.
 */
const applyFreshSource = (entry, { trainerId, trainerName, startTime, endTime }) => {
  const start = startTime ? String(startTime).slice(0, 5) : entry.startTime;
  const end = endTime ? String(endTime).slice(0, 5) : entry.endTime;
  return {
    ...entry,
    trainerId: trainerId ?? entry.trainerId,
    trainerName: trainerName ?? entry.trainerName,
    startTime: start,
    endTime: end,
    durationMinutes: Math.max(10, toMinutes(end) - toMinutes(start)),
  };
};

// 수업 상세는 회차(classTimes)별 시간을 갖는다 — 복사한 회차를 우선, 회차가 하나뿐이면 그것을 쓴다
const freshFromLesson = (entry, lesson) => {
  const times = lesson?.classTimes || [];
  const time = times.find(item => item.courseClassTimeId === entry.courseClassTimeId) || (times.length === 1 ? times[0] : null);
  return applyFreshSource(entry, {
    trainerId: lesson?.trainerId,
    trainerName: lesson?.trainerName,
    startTime: time?.startTime,
    endTime: time?.endTime,
  });
};

const freshFromConsultation = (entry, consultation) =>
  applyFreshSource(entry, {
    trainerId: consultation?.managerId,
    trainerName: consultation?.managerName,
    startTime: consultation?.consultStartTime,
    endTime: consultation?.consultEndTime,
  });

// 최신 원본 시간 기준으로 다시 본 과거 판정 — pasteSchedule 입구의 판정은 복사 시점 스냅샷이라 여기서 한 번 더 막는다
const assertNotPast = (entry, target) => {
  if (isPastPasteTarget(entry, target)) throw new Error(PAST_PASTE_MESSAGE);
};

/**
 * 개인·듀엣 사전확인 — 대상 강사가 회원 수강권의 그 지점 담당강사인지 본다.
 * needsAssign=true 면 호출부가 "담당강사로 추가할지" 물어본다.
 */
export const checkTrainerAssignment = async ({ entry, target, token, branchId }) => {
  if (!PRIVATE_KINDS.includes(entry.kind) && entry.kind !== 'OT') return { needsAssign: false, targets: [] };

  // 목적지 강사 지정이 없으면 원본 담당자 그대로라 물어볼 것이 없다.
  if (!target.trainerId) return { needsAssign: false, targets: [] };

  const lesson = await getLessonById(entry.courseClassId, token);
  const { trainerId } = resolveTrainer(freshFromLesson(entry, lesson), target);
  // 원본의 현재 담당자와 같으면 물어볼 것이 없다.
  if (!trainerId || trainerId === lesson?.trainerId) return { needsAssign: false, targets: [] };
  // OT·듀엣은 함께 붙여넣을 회원 전원을 각자 판정한다(개인레슨과 같은 담당강사 배정 규칙).
  const otMemberIds =
    entry.kind === 'OT' || lesson?.courseClassType === 'DUET_LESSON'
      ? [...new Set((await loadGroupPasteMembers({ entry, token })).map(member => member.memberId))]
      : null;
  return checkPasteTrainer(
    {
      courseClassType: lesson?.courseClassType || entry.kind,
      memberIds: otMemberIds,
      memberId: otMemberIds?.[0] || lesson?.memberId || entry.memberId,
      tickets: productTicketIds(lesson),
      classDate: target.date,
      trainerId,
      branchId,
      courseTicketDeductionCount: lesson?.courseTicketDeductionCount ?? 1,
    },
    token,
  );
};

/**
 * 일간 드래그 이동(개인·듀엣·OT)에서 새 강사가 담당강사인지 확인한다.
 * 백엔드가 그 수업의 실제 예약 티켓·수업 지점 기준으로 판정하므로 수업 id 와 새 강사 id 만 보낸다.
 */
export const checkMoveTrainerAssignment = ({ courseClassId, trainerId, token }) =>
  checkPasteTrainer({ courseClassId, trainerId }, token);

const pastePrivateLesson = async ({ entry, target, token, branchId, assignTrainer, force }) => {
  const lesson = await getLessonById(entry.courseClassId, token);
  const fresh = freshFromLesson(entry, lesson);
  assertNotPast(fresh, target);
  const { startTime, endTime } = resolveTimes(fresh, target);
  const { trainerId } = resolveTrainer(fresh, target);
  const tickets = productTicketIds(lesson);
  const trials = trialTicketIds(lesson);
  requireTickets(tickets, trials);
  // 듀엣은 원본에 예약된 회원 그대로(1명이면 1명) 옮긴다 — 상대를 강제하지 않는다(11/3).
  const isDuet = lesson?.courseClassType === 'DUET_LESSON';
  const duetMemberIds = isDuet
    ? [...new Set((await loadGroupPasteMembers({ entry, token })).map(member => member.memberId))]
    : [];
  if (isDuet && duetMemberIds.length === 0) {
    throw new Error('원본 듀엣에 예약된 회원이 없어 붙여넣을 수 없습니다.');
  }

  await createLesson(
    {
      branchId,
      courseClassType: isDuet ? 'DUET_LESSON' : 'PRIVATE_LESSON',
      className: lesson?.className,
      exerciseRoomId: lesson?.branchExerciseRoomId ?? -1,
      registrationStartDate: target.date,
      registrationEndDate: target.date,
      trainerId,
      memberId: isDuet ? duetMemberIds[0] : lesson?.memberId,
      ...(isDuet ? { memberIds: duetMemberIds } : {}),
      membersMaxCount: lesson?.membersMaxCount,
      courseTicketDeductionCount: lesson?.courseTicketDeductionCount ?? 1,
      memo: lesson?.memo || '',
      tickets,
      trialTickets: trials,
      classTimeInfoList: [{ dayOfWeek: dayOfWeekOf(target.date), startTime, endTime }],
      assignTrainerToTicketIfMissing: !!assignTrainer,
    },
    token,
    force,
  );

  return { kind: entry.kind };
};

/**
 * OT — 개인레슨처럼 원본 회원을 그대로 옮긴다. 원본 회차의 예약 회원 전원(대기·취소·탈퇴 제외)을
 * 새 OT 에 함께 예약하고, 회원마다 수업에 연결된 OT 수강권/일일권을 백엔드가 자동으로 고른다.
 */
const pasteOtLesson = async ({ entry, target, token, branchId, assignTrainer, force }) => {
  const lesson = await getLessonById(entry.courseClassId, token);
  const fresh = freshFromLesson(entry, lesson);
  assertNotPast(fresh, target);
  const { startTime, endTime } = resolveTimes(fresh, target);
  const { trainerId } = resolveTrainer(fresh, target);
  const tickets = productTicketIds(lesson);
  const trials = trialTicketIds(lesson);
  requireTickets(tickets, trials);

  const members = await loadGroupPasteMembers({ entry, token });
  const memberIds = [...new Set(members.map(member => member.memberId))];
  if (memberIds.length === 0) {
    throw new Error('원본 OT 에 예약된 회원이 없어 붙여넣을 수 없습니다.');
  }
  // 정원은 원본 그대로(회원이 정원보다 적어도 된다). 원본 정원보다 회원이 많을 수는 없지만 방어적으로 맞춘다.
  const capacity = Math.max(lesson?.membersMaxCount ?? entry.capacity ?? memberIds.length, memberIds.length);

  await createLesson(
    {
      branchId,
      courseClassType: 'OT',
      className: lesson?.className,
      exerciseRoomId: lesson?.branchExerciseRoomId ?? -1,
      registrationStartDate: target.date,
      registrationEndDate: target.date,
      trainerId,
      memberId: memberIds[0],
      memberIds,
      membersMaxCount: capacity,
      assignTrainerToTicketIfMissing: !!assignTrainer,
      courseTicketDeductionCount: lesson?.courseTicketDeductionCount ?? 1,
      memo: lesson?.memo || '',
      tickets,
      trialTickets: trials,
      classTimeInfoList: [{ dayOfWeek: dayOfWeekOf(target.date), startTime, endTime }],
    },
    token,
    force,
  );

  return { kind: entry.kind };
};

/**
 * 그룹 붙여넣기에서 함께 예약할 수 있는 원본 회차 회원.
 * 대기·취소 예약은 제외하고(출석·결석은 포함), 탈퇴·차단 회원도 뺀다 —
 * 동시 예약은 ACTIVE 회원만 받아서(백엔드가 하나라도 섞이면 요청 전체를 거부한다) 화면이 미리 걸러야 한다.
 */
export const loadGroupPasteMembers = async ({ entry, token }) => {
  const members = await getCourseClassTimeMembers(entry.courseClassTimeId, token);
  return (members || [])
    .filter(member => member?.status !== 'WAITING' && member?.status !== 'CANCELED')
    .filter(member => member?.memberStatus === 'ACTIVE')
    .filter(member => member?.memberId);
};

/**
 * 그룹수업 회차 1건의 생성 본문. 미리보기(group-reserve/preview)와 생성이 같은 본문을 써야 판정이 같다.
 * members 가 있으면 그 회원들을 함께 예약한다(지난 시간으로는 붙여넣을 수 없어 출결 상태는 싣지 않는다).
 */
const buildGroupPastePayload = async ({ entry, target, token, branchId, members = [] }) => {
  const lesson = await getLessonById(entry.courseClassId, token);
  const fresh = freshFromLesson(entry, lesson);
  assertNotPast(fresh, target);
  const { startTime, endTime } = resolveTimes(fresh, target);
  const { trainerId } = resolveTrainer(fresh, target);
  const tickets = productTicketIds(lesson);
  const trials = trialTicketIds(lesson);
  requireTickets(tickets, trials);

  const memberIds = [...new Set(members.map(member => member.memberId))];
  const capacity = lesson?.membersMaxCount;
  // 정원을 넘겨 보내면 백엔드가 요청 전체를 거부한다(부분 예약이 없다) — 무엇을 하면 되는지 먼저 알려준다.
  if (memberIds.length > 0 && capacity != null && memberIds.length > capacity) {
    throw new Error(
      `예약 회원 ${memberIds.length}명이 수업 정원 ${capacity}명을 넘어 회원까지 붙여넣을 수 없습니다. [일정만 붙여넣기]로 만든 뒤 참석관리에서 회원을 넣어 주세요.`,
    );
  }
  return {
    memberIds,
    payload: {
      branchId,
      courseClassType: 'GROUP_LESSON',
      className: lesson?.className,
      exerciseRoomId: lesson?.branchExerciseRoomId ?? -1,
      registrationStartDate: target.date,
      registrationEndDate: target.date,
      trainerId,
      tickets,
      trialTickets: trials,
      membersMaxCount: capacity,
      membersWaitMaxCount: lesson?.membersWaitMaxCount ?? 0,
      courseTicketDeductionCount: lesson?.courseTicketDeductionCount ?? 1,
      memo: lesson?.memo || '',
      openYn: lesson?.openYn || 'N',
      openScheduledYn: lesson?.openScheduledYn || 'N',
      openScheduleType: lesson?.openScheduleType || null,
      openScheduledDay: lesson?.openScheduledDay || null,
      openScheduledTime: lesson?.openScheduledTime || null,
      closeScheduledYn: lesson?.closeScheduledYn || 'N',
      closeScheduledMinCount: lesson?.closeScheduledMinCount ?? null,
      closeScheduledTime: lesson?.closeScheduledTime || null,
      allTicketsSelectedYn: lesson?.allTicketsSelectedYn || 'N',
      templateId: lesson?.templateId || null,
      classTimeInfoList: [{ dayOfWeek: dayOfWeekOf(target.date), startTime, endTime }],
      reserveMemberIds: memberIds,
    },
  };
};

/**
 * 회원까지 붙여넣기 전 미리보기 — 새 회차 날짜 기준으로 예약 가능 여부·제외 사유를 받는다(저장 없음).
 */
export const previewGroupPaste = async ({ entry, target, token, branchId, members, force }) => {
  const { payload } = await buildGroupPastePayload({ entry, target, token, branchId, members });
  const preview = await previewGroupReserve(payload, token, force);
  return { members: preview?.members || [] };
};

/**
 * 그룹수업 — 회차 1건을 만든다. members 가 있으면 그 회원들을 새 회차에 예약중으로 함께 넣는다.
 * 잔여·기간 때문에 예약이 안 되는 회원은 백엔드가 제외하고 사유를 돌려준다.
 */
const pasteGroupLesson = async ({ entry, target, token, branchId, members = [], force }) => {
  const { payload, memberIds } = await buildGroupPastePayload({ entry, target, token, branchId, members });
  const response = await createLesson(payload, token, force);

  return {
    kind: entry.kind,
    requestedMemberCount: memberIds.length,
    memberResults: response?.members || [],
    failureCount: response?.failureCount ?? 0,
  };
};

/** 상담 — 담당자와 날짜·시간만 바꾼다. 결과는 미래 날짜면 '예정'으로 초기화, 과거면 원본 그대로. */
const pasteConsultation = async ({ entry, target, token, branchId, force }) => {
  const consultation = await getConsultationById(entry.consultationId, token);
  const fresh = freshFromConsultation(entry, consultation);
  assertNotPast(fresh, target);
  const { startTime, endTime } = resolveTimes(fresh, target);
  const { trainerId, trainerName } = resolveTrainer(fresh, target);
  const isFuture = !moment(target.date, 'YYYY-MM-DD').isBefore(moment().startOf('day'));

  await createConsultation(
    {
      branchId,
      consultType: consultation?.consultType || 'MEMBER',
      memberId: consultation?.memberId,
      temporaryUserId: consultation?.temporaryUserId,
      name: consultation?.name,
      phone: consultation?.phone,
      title: consultation?.title,
      consultDate: target.date,
      consultStartTime: startTime,
      consultEndTime: endTime,
      consultMethod: consultation?.consultMethod || null,
      consultPurpose: consultation?.consultPurpose || null,
      consultResult: isFuture ? 'SCHEDULED' : consultation?.consultResult || 'SCHEDULED',
      visitPath: consultation?.visitPath,
      visitPathEtc: consultation?.visitPathEtc,
      exercisePurposes: consultation?.exercisePurposes,
      managerId: trainerId ? Number(trainerId) : null,
      managerName: trainerName || '',
      content: consultation?.content || '',
      exerciseRoomId: consultation?.exerciseRoomId ?? null,
    },
    token,
    force,
  );

  return { kind: entry.kind };
};

/** 기타 — GET 원본(최신)을 읽어 담당자와 날짜·시간만 바꾼다. */
const pasteBranchSchedule = async ({ entry, target, token, branchId }) => {
  const schedule = await getBranchScheduleById(entry.branchScheduleId, token);
  const fresh = applyFreshSource(entry, {
    trainerId: schedule?.employeeId,
    trainerName: schedule?.employeeName,
    startTime: schedule?.startTime,
    endTime: schedule?.endTime,
  });
  assertNotPast(fresh, target);
  const { startTime, endTime } = resolveTimes(fresh, target);
  const { trainerId, trainerName } = resolveTrainer(fresh, target);

  await createBranchSchedule(
    {
      branchId,
      employeeId: trainerId,
      employeeName: trainerName,
      title: schedule?.title ?? entry.title,
      scheduleDate: target.date,
      startTime,
      endTime,
      content: schedule?.content ?? null,
    },
    token,
  );

  return { kind: entry.kind };
};

/**
 * 붙여넣기 실행.
 *
 * @param target {{ date:string, startHour:number|null, trainerId:number|null, trainerName:string }}
 * @param options {{ members?:object[], assignTrainer?:boolean, force?:boolean }} members 는 loadGroupPasteMembers 결과(회원ID·출결 상태)
 *   force 는 상담이 낀 일정 겹침을 사용자가 확인한 뒤 다시 보낼 때만 true(withOverlapConfirm)
 */
export const pasteSchedule = async ({ entry, target, token, branchId, options = {} }) => {
  const args = { entry, target, token, branchId, force: !!options.force };

  if (isPastPasteTarget(entry, target)) {
    throw new Error(PAST_PASTE_MESSAGE);
  }

  if (PRIVATE_KINDS.includes(entry.kind)) {
    return pastePrivateLesson({ ...args, assignTrainer: options.assignTrainer });
  }
  if (entry.kind === 'OT') {
    return pasteOtLesson({ ...args, assignTrainer: options.assignTrainer });
  }
  if (entry.kind === 'GROUP_LESSON') {
    return pasteGroupLesson({ ...args, members: options.members || [] });
  }
  if (entry.kind === 'CONSULTATION') {
    return pasteConsultation(args);
  }
  if (entry.kind === 'ETC') {
    return pasteBranchSchedule(args);
  }
  throw new Error('붙여넣을 수 없는 일정입니다.');
};
